/**
 * OpsNewsletterPage — internal admin view of newsletter subscribers.
 *
 * Features:
 *  - Search by email substring
 *  - Filter by status (all / active / unsubscribed)
 *  - Toggle unsubscribe status
 *  - Hard-delete a subscriber
 *  - Export current filtered list as CSV (opens Excel cleanly)
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  DownloadSimple,
  Trash,
  Prohibit,
  ArrowClockwise,
  PaperPlaneTilt,
  X,
} from '@phosphor-icons/react';
import OpsLayout from './OpsLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  active: 'نشط',
  unsubscribed: 'أُلغي الاشتراك',
};

const SOURCE_LABEL = {
  footer: 'التذييل',
  knowledge: 'مركز المعرفة',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ar-SA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const OpsNewsletterPage = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, active: 0, unsubscribed: 0 });
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [bSubject, setBSubject] = useState('');
  const [bHtml, setBHtml] = useState('');
  const [bSending, setBSending] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, status, limit: '500' });
      const res = await fetch(
        `${API_URL}/api/ops/newsletter/subscribers?${params.toString()}`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error('تعذّر جلب قائمة المشتركين');
      const data = await res.json();
      setItems(data.items || []);
      setCounts(data.counts || { all: 0, active: 0, unsubscribed: 0 });
    } catch (e) {
      toast.error(e.message || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [status]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchList();
  };

  const toggleUnsub = async (sub) => {
    setBusyId(sub.id);
    try {
      const res = await fetch(
        `${API_URL}/api/ops/newsletter/subscribers/${sub.id}/unsubscribe`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error('تعذّر تعديل الحالة');
      const data = await res.json();
      toast.success(
        data.status === 'unsubscribed'
          ? 'تم إلغاء اشتراك المستخدم'
          : 'تم إعادة تفعيل الاشتراك',
      );
      await fetchList();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteOne = async (sub) => {
    if (!window.confirm(`تأكيد حذف نهائي للمشترك ${sub.email}؟`)) return;
    setBusyId(sub.id);
    try {
      const res = await fetch(
        `${API_URL}/api/ops/newsletter/subscribers/${sub.id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok) throw new Error('تعذّر الحذف');
      toast.success('تم الحذف نهائيًا');
      await fetchList();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ q, status });
    // Trigger a real download via anchor. Credentials are sent by the
    // browser because same-origin cookie is used for the ops session.
    const url = `${API_URL}/api/ops/newsletter/subscribers.csv?${params.toString()}`;
    // Use fetch → blob to preserve cookies and force a download in-page.
    (async () => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error('تعذّر تصدير الملف');
        const blob = await res.blob();
        const objUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        const cd = res.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename="?([^"]+)"?/);
        a.download = match ? match[1] : 'newsletter_subscribers.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(objUrl);
        toast.success('تم تنزيل الملف');
      } catch (e) {
        toast.error(e.message);
      }
    })();
  };

  const empty = !loading && items.length === 0;
  const totalLabel = useMemo(
    () =>
      `${items.length} من أصل ${counts.all} — نشِط: ${counts.active} • ملغى: ${counts.unsubscribed}`,
    [items.length, counts],
  );

  const sendBroadcast = async () => {
    const subject = bSubject.trim();
    const html = bHtml.trim();
    if (!subject || !html) {
      toast.error('يرجى تعبئة الموضوع ومحتوى النشرة');
      return;
    }
    if (
      !window.confirm(
        `تأكيد إرسال النشرة إلى جميع المشتركين النشطين (${counts.active})؟`,
      )
    )
      return;
    setBSending(true);
    try {
      const res = await fetch(`${API_URL}/api/ops/newsletter/broadcast`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, html }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'تعذّر إرسال النشرة');
      toast.success(
        `تم الإرسال: ${data.sent_count} نجحت، ${data.failed_count} فشلت من أصل ${data.recipients_count}.`,
      );
      setBroadcastOpen(false);
      setBSubject('');
      setBHtml('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBSending(false);
    }
  };

  return (
    <OpsLayout
      title="مشتركو النشرة البريدية"
      subtitle="Newsletter subscribers"
    >
      <div className="space-y-5" data-testid="ops-newsletter-page" dir="rtl">
        {/* Toolbar */}
        <div className="bg-white rounded-2xl border border-[#E6E0D6] p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <form
              onSubmit={onSearch}
              className="flex items-center gap-2 flex-1 min-w-0"
            >
              <div className="relative flex-1">
                <MagnifyingGlass
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#92A082]"
                />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="newsletter-search"
                  placeholder="ابحث بالبريد الإلكتروني…"
                  className="w-full bg-[#FAFAF7] border border-[#D8D2C8] rounded-xl pr-10 pl-3 py-2.5 text-sm focus:outline-none focus:border-[#4A641F]"
                />
              </div>
              <button
                type="submit"
                data-testid="newsletter-search-btn"
                className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-4 py-2.5 text-sm font-bold"
              >
                بحث
              </button>
              <button
                type="button"
                onClick={fetchList}
                title="تحديث"
                data-testid="newsletter-refresh-btn"
                className="border border-[#D8D2C8] text-[#3D512D] hover:bg-[#F5F2EC] rounded-xl px-3 py-2.5"
              >
                <ArrowClockwise size={16} />
              </button>
            </form>

            <div className="flex items-center gap-2">
              <div
                className="inline-flex bg-[#F5F2EC] rounded-xl p-1 text-xs"
                data-testid="newsletter-status-filter"
              >
                {[
                  { v: 'all', l: `الكل (${counts.all})` },
                  { v: 'active', l: `نشِط (${counts.active})` },
                  { v: 'unsubscribed', l: `ملغى (${counts.unsubscribed})` },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setStatus(opt.v)}
                    data-testid={`filter-${opt.v}`}
                    className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
                      status === opt.v
                        ? 'bg-white text-[#1F3D2B] shadow-sm'
                        : 'text-[#5D6E55] hover:text-[#1F3D2B]'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={exportCsv}
                data-testid="newsletter-export-btn"
                className="inline-flex items-center gap-1.5 bg-[#4A641F] hover:bg-[#3B5626] text-white rounded-xl px-4 py-2.5 text-sm font-bold"
              >
                <DownloadSimple size={16} weight="bold" />
                تصدير CSV
              </button>
              <button
                type="button"
                onClick={() => setBroadcastOpen(true)}
                data-testid="newsletter-broadcast-btn"
                className="inline-flex items-center gap-1.5 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-4 py-2.5 text-sm font-bold"
              >
                <PaperPlaneTilt size={16} weight="bold" />
                إرسال نشرة جديدة
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-[#92A082]">{totalLabel}</p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#E6E0D6] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F2EC] text-[#3D512D]">
                <tr>
                  <th className="text-right font-bold px-4 py-3 whitespace-nowrap">
                    البريد الإلكتروني
                  </th>
                  <th className="text-right font-bold px-4 py-3 whitespace-nowrap">
                    المصدر
                  </th>
                  <th className="text-right font-bold px-4 py-3 whitespace-nowrap">
                    الحالة
                  </th>
                  <th className="text-right font-bold px-4 py-3 whitespace-nowrap">
                    تاريخ الاشتراك
                  </th>
                  <th className="text-right font-bold px-4 py-3 whitespace-nowrap">
                    تاريخ الإلغاء
                  </th>
                  <th className="text-right font-bold px-4 py-3 whitespace-nowrap">
                    إجراءات
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="text-center px-4 py-10 text-[#92A082]">
                      جارٍ التحميل…
                    </td>
                  </tr>
                )}
                {empty && (
                  <tr>
                    <td colSpan={6} className="text-center px-4 py-10 text-[#92A082]">
                      لا يوجد مشتركون مطابقون.
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((sub) => (
                    <tr
                      key={sub.id}
                      data-testid={`sub-row-${sub.email}`}
                      className="border-t border-[#EFEAE1] hover:bg-[#FAFAF7]"
                    >
                      <td className="px-4 py-3 font-mono text-[13px] text-[#1F3D2B] break-all" dir="ltr">
                        {sub.email}
                      </td>
                      <td className="px-4 py-3 text-[#5D6E55]">
                        {SOURCE_LABEL[sub.source] || sub.source}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${
                            sub.status === 'active'
                              ? 'bg-[#E9F0DA] text-[#4A641F]'
                              : 'bg-[#F5E7E4] text-[#B24A2C]'
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              sub.status === 'active' ? 'bg-[#4A641F]' : 'bg-[#B24A2C]'
                            }`}
                          />
                          {STATUS_LABEL[sub.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#5D6E55] text-[13px] whitespace-nowrap">
                        {formatDate(sub.subscribed_at)}
                      </td>
                      <td className="px-4 py-3 text-[#5D6E55] text-[13px] whitespace-nowrap">
                        {formatDate(sub.unsubscribed_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleUnsub(sub)}
                            disabled={busyId === sub.id}
                            data-testid={`sub-toggle-${sub.email}`}
                            className="inline-flex items-center gap-1 border border-[#D8D2C8] text-[#3D512D] hover:bg-[#F5F2EC] rounded-lg px-2.5 py-1.5 text-xs disabled:opacity-50"
                            title={
                              sub.status === 'active'
                                ? 'إلغاء الاشتراك'
                                : 'إعادة التفعيل'
                            }
                          >
                            <Prohibit size={14} />
                            {sub.status === 'active' ? 'إلغاء' : 'تفعيل'}
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteOne(sub)}
                            disabled={busyId === sub.id}
                            data-testid={`sub-delete-${sub.email}`}
                            className="inline-flex items-center gap-1 border border-[#EAD3CC] text-[#B24A2C] hover:bg-[#F9EFEB] rounded-lg px-2.5 py-1.5 text-xs disabled:opacity-50"
                            title="حذف نهائي"
                          >
                            <Trash size={14} />
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {broadcastOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && setBroadcastOpen(false)}
        >
          <div
            className="bg-white rounded-2xl border border-[#E6E0D6] w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl"
            data-testid="newsletter-broadcast-modal"
            dir="rtl"
          >
            <div className="sticky top-0 bg-white border-b border-[#EFEAE1] flex items-center justify-between px-6 py-4 z-10">
              <h3 className="text-lg font-bold text-[#1F3D2B] font-['IBM_Plex_Sans_Arabic'] flex items-center gap-2">
                <PaperPlaneTilt size={20} weight="duotone" className="text-[#4A641F]" />
                إرسال نشرة بريدية جديدة
              </h3>
              <button
                type="button"
                onClick={() => setBroadcastOpen(false)}
                className="text-[#92A082] hover:text-[#1F3D2B]"
                aria-label="إغلاق"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-[#F5F2EC] border border-[#E6E0D6] rounded-xl px-4 py-3 text-xs text-[#5D6E55] leading-7">
                سترسل النشرة إلى <strong>{counts.active}</strong> مشترك
                نشط فقط. الرسائل ملغاة الاشتراك مستثناة تلقائيًا. المحتوى
                HTML سيُغلَّف بقالب Childhood Horizons مع تذييل إلغاء اشتراك.
              </div>

              <div>
                <label className="text-sm font-medium text-[#1F3D2B] block mb-1.5">
                  موضوع النشرة
                </label>
                <input
                  type="text"
                  value={bSubject}
                  onChange={(e) => setBSubject(e.target.value)}
                  data-testid="broadcast-subject-input"
                  placeholder="مثال: مستجدات مركز المعرفة — أبريل"
                  className="w-full bg-[#FAFAF7] border border-[#D8D2C8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#4A641F]"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-[#1F3D2B]">
                    محتوى النشرة (HTML)
                  </label>
                  <span className="text-[11px] text-[#92A082]">
                    يدعم HTML: {'<p>'} {'<h3>'} {'<strong>'} {'<a href="…">'} {'<ul><li>'}
                  </span>
                </div>
                <textarea
                  value={bHtml}
                  onChange={(e) => setBHtml(e.target.value)}
                  rows={14}
                  data-testid="broadcast-html-input"
                  placeholder={
                    '<h3>عنوان القسم</h3>\n<p>نصّ الفقرة الافتتاحية…</p>\n<ul><li>نقطة أولى</li><li>نقطة ثانية</li></ul>\n<p><a href="https://childhoodhorizons.com/knowledge">اقرأ المزيد</a></p>'
                  }
                  className="w-full bg-[#FAFAF7] border border-[#D8D2C8] rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-[#4A641F] leading-7"
                  dir="ltr"
                />
              </div>

              {bHtml.trim() && (
                <div>
                  <p className="text-sm font-medium text-[#1F3D2B] mb-1.5">
                    معاينة
                  </p>
                  <div
                    className="bg-white border border-[#E6E0D6] rounded-xl p-4 text-sm text-[#3D512D] leading-8 max-h-64 overflow-y-auto"
                    // Preview is only rendered inside the ops-admin scope,
                    // so trust the operator's own HTML.
                    dangerouslySetInnerHTML={{ __html: bHtml }}
                  />
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-[#EFEAE1] px-6 py-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setBroadcastOpen(false)}
                className="border border-[#D8D2C8] text-[#3D512D] hover:bg-[#F5F2EC] rounded-xl px-5 py-2.5 text-sm font-medium"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={sendBroadcast}
                disabled={bSending}
                data-testid="broadcast-send-btn"
                className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
              >
                <PaperPlaneTilt size={16} weight="bold" />
                {bSending
                  ? 'جارٍ الإرسال…'
                  : `إرسال إلى ${counts.active} مشترك نشط`}
              </button>
            </div>
          </div>
        </div>
      )}
    </OpsLayout>
  );
};

export default OpsNewsletterPage;
